'use client';

import {
  Button,
  Drawer,
  IconButton,
  Switch,
  Text,
  toasterApi,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { CancelCircleIcon, Cancel01Icon } from '@terra/ui-icons';
import { useCallback, useMemo, useState } from 'react';
import type {
  AiCreateSkillRejectedResponse,
  SkillDto,
} from '@terra/shared-contracts-ai-skills';
import { useSkillsMutations } from '../hooks/use-skills';
import { SkillsApiError } from '../internal/terra-api-wrapper';
import type { SkillsEditorLabels } from '../types';
import {
  SkillEditorForm,
  getSkillEditorValidation,
  type SkillEditorFormDraft,
  type SkillEditorTouchedFields,
} from './skill-editor-form';

type Mode = 'create' | 'edit-draft';
const TOUCHED_ALL: SkillEditorTouchedFields = {
  slug: true,
  name: true,
  description: true,
  body: true,
};
const TOUCHED_NONE: SkillEditorTouchedFields = {
  slug: false,
  name: false,
  description: false,
  body: false,
};

function isAiCreateRejection(
  value: unknown,
): value is AiCreateSkillRejectedResponse {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<AiCreateSkillRejectedResponse>;

  return (
    typeof candidate.reason === 'string' &&
    typeof candidate.message === 'string'
  );
}

function getInitialDraft(skill?: SkillDto): SkillEditorFormDraft {
  const seedVersion = skill?.draftVersion ?? skill?.publishedVersion ?? null;

  return {
    slug: skill?.slug ?? '',
    name: seedVersion?.frontmatter.name ?? '',
    description: seedVersion?.frontmatter.description ?? '',
    body: seedVersion?.body ?? '',
  };
}

export function SkillEditorDrawer({
  mode,
  skill,
  labels,
  applicationId,
  onClose,
}: {
  mode: Mode;
  skill?: SkillDto;
  labels: SkillsEditorLabels;
  applicationId: string;
  onClose: () => void;
}) {
  const [draft] = useState<SkillEditorFormDraft>(() => getInitialDraft(skill));
  const [publishImmediately, setPublishImmediately] = useState(false);
  const [touched, setTouched] =
    useState<SkillEditorTouchedFields>(TOUCHED_NONE);
  const [creatorError, setCreatorError] =
    useState<AiCreateSkillRejectedResponse | null>(null);
  const [initialDraft] = useState<SkillEditorFormDraft>(() =>
    getInitialDraft(skill),
  );
  const { aiCreateSkill, updateSkillDraft, publishSkill } = useSkillsMutations({
    applicationId,
  });
  const editorMode = mode === 'create' ? 'create' : 'edit';
  const validation = useMemo(
    () =>
      getSkillEditorValidation({
        draft,
        touched,
        mode: editorMode,
        labels,
      }),
    [draft, editorMode, labels, touched],
  );
  const isPending =
    aiCreateSkill.isPending ||
    updateSkillDraft.isPending ||
    publishSkill.isPending;
  const isDirty =
    draft.slug !== initialDraft.slug ||
    draft.name !== initialDraft.name ||
    draft.description !== initialDraft.description ||
    draft.body !== initialDraft.body;
  const handleRefineField = useCallback(() => undefined, []);
  const handleSave = async () => {
    setCreatorError(null);
    setTouched(TOUCHED_ALL);
    if (validation.hasBlockingErrors) {
      return;
    }

    try {
      if (skill) {
        await updateSkillDraft.mutateAsync({
          skillId: skill.skillId,
          body: {
            frontmatter: {
              name: draft.name.trim(),
              description: draft.description.trim(),
            },
            body: draft.body,
          },
        });
        if (publishImmediately) {
          await publishSkill.mutateAsync({ skillId: skill.skillId });
        }
      } else {
        await aiCreateSkill.mutateAsync({
          applicationId,
          slug: draft.slug.trim(),
          frontmatter: {
            name: draft.name.trim(),
            description: draft.description.trim(),
          },
          body: draft.body,
          publishImmediately,
        });
      }

      toasterApi.success({
        title: labels.successTitle,
        description: labels.saved,
      });
      onClose();
    } catch (error) {
      if (error instanceof SkillsApiError && isAiCreateRejection(error.body)) {
        setCreatorError(error.body);
        toasterApi.error({
          title: labels.aiCreateRejectedTitle,
          description: error.body.message,
        });

        return;
      }

      toasterApi.error({
        title: labels.errorTitle,
        description: labels.error,
      });
    }
  };

  return (
    <Drawer.Root open onOpenChange={({ open }) => !open && onClose()}>
      <Drawer.Backdrop />
      <Drawer.Positioner w="95vw" maxW="1100px">
        <Drawer.Content h="full" overflow="hidden">
          <Drawer.Header>
            <VStack alignItems="flex-start" gap={0.5} pr={8}>
              <Drawer.Title>
                {mode === 'create' ? labels.createTitle : labels.editTitle}
              </Drawer.Title>
              <Text fontSize="sm" color="fg.muted">
                {mode === 'create'
                  ? labels.createSubtitle
                  : labels.editSubtitle}
              </Text>
            </VStack>
            <Drawer.CloseTrigger asChild position="absolute" top="3" right="4">
              <IconButton variant="ghost" aria-label={labels.close} size="sm">
                <Cancel01Icon size={14} />
              </IconButton>
            </Drawer.CloseTrigger>
          </Drawer.Header>
          <Drawer.Body overflow="auto">
            <VStack gap={6} alignItems="stretch">
              {creatorError ? (
                <CreatorErrorPanel
                  title={labels.aiCreateRejectedTitle}
                  error={creatorError}
                />
              ) : null}
              <SkillEditorForm
                mode={editorMode}
                draft={draft}
                labels={labels}
                touched={touched}
                onRefineField={handleRefineField}
              />
              <VStack
                gap={3}
                alignItems="stretch"
                borderRadius="md"
                borderWidth="1px"
                borderStyle="solid"
                borderColor="border.default"
                bg="bg.subtle"
                p={3}
              >
                <HStack
                  gap={3}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <VStack alignItems="flex-start" gap={0.5} flex="1" minW="0">
                    <Text fontSize="sm" fontWeight="medium" color="fg.default">
                      {labels.publishImmediately}
                    </Text>
                    <Text fontSize="xs" color="fg.muted" lineHeight="1.6">
                      {labels.publishImmediatelyHelp}
                    </Text>
                  </VStack>
                  <Switch
                    checked={publishImmediately}
                    onCheckedChange={({ checked }) =>
                      setPublishImmediately(Boolean(checked))
                    }
                  />
                </HStack>
              </VStack>
            </VStack>
          </Drawer.Body>
          <Drawer.Footer
            gap={2}
            p={4}
            borderTopWidth="1px"
            borderTopColor="border.default"
            bg="bg.subtle"
          >
            <HStack
              gap={2}
              w="full"
              justifyContent="space-between"
              alignItems="center"
            >
              <Text fontSize="2xs" color="fg.muted">
                {isDirty ? labels.unsavedChangesNote : ''}
              </Text>
              <HStack gap={2}>
                <Drawer.CloseTrigger asChild>
                  <Button variant="outline" size="sm" disabled={isPending}>
                    {labels.cancel}
                  </Button>
                </Drawer.CloseTrigger>
                <Button
                  onClick={handleSave}
                  loading={isPending}
                  disabled={validation.hasBlockingErrors}
                  size="sm"
                  title={mode === 'create' ? labels.aiCreateHelp : undefined}
                >
                  {mode === 'create'
                    ? labels.aiCreate
                    : publishImmediately
                      ? labels.publish
                      : labels.saveDraft}
                </Button>
              </HStack>
            </HStack>
          </Drawer.Footer>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}

function CreatorErrorPanel({
  title,
  error,
}: {
  title: string;
  error: AiCreateSkillRejectedResponse;
}) {
  return (
    <VStack
      alignItems="stretch"
      gap={2}
      borderRadius="md"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.error"
      bg="bg.subtle"
      p={3}
    >
      <HStack alignItems="flex-start" gap={2}>
        <Box color="fg.error" borderRadius="full" p={0.5} mt={0.5}>
          <CancelCircleIcon size={14} />
        </Box>
        <VStack alignItems="flex-start" gap={0.5} flex="1" minW="0">
          <Text fontSize="xs" fontWeight="semibold" color="fg.error">
            {title}
          </Text>
          <Text fontSize="xs" color="fg.muted" lineHeight="1.6">
            {error.message}
          </Text>
        </VStack>
      </HStack>
      {error.details?.length ? (
        <VStack alignItems="stretch" gap={1} pl={6}>
          {error.details.map((detail) => (
            <HStack key={detail} alignItems="flex-start" gap={2}>
              <Box
                mt={1.5}
                w="3px"
                h="3px"
                borderRadius="full"
                bg="fg.error"
                flexShrink={0}
              />
              <Text fontSize="xs" color="fg.default" lineHeight="1.6">
                {detail}
              </Text>
            </HStack>
          ))}
        </VStack>
      ) : null}
    </VStack>
  );
}
